import { useReducer } from 'react';
import { createActionCreators, createReducerFunction, ImmerReducer } from 'immer-reducer';
import { RequestGuideAnswer } from 'resources/CustomerIntakeFlow/requestGuide';
import { v4 } from 'uuid';

import { PriceBookIndustry } from 'components/CustomerIntakeOnboarding/IndustriesStep';
import { ANSWER_TYPES, QUESTION_TYPES } from 'components/CustomerIntakeSettings/requestGuide/consts';
import { replaceDash } from 'components/CustomerIntakeSettings/requestGuide/QuestionContent';
import {
  AnswerTypes,
  QuestionState,
  QuestionTypes,
  ServiceType,
  Switchable,
} from 'components/CustomerIntakeSettings/requestGuide/types';

const getInitialState = (): QuestionState => ({
  answers: [
    {
      uid: replaceDash(v4()),
      value: '',
    },
  ],
  answerType: ANSWER_TYPES.custom,
  question: '',
  reminder: '',
  type: 'single-select',
  initialIndustries: [],
  initialServiceTypes: [],
  serviceTypes: [],
  industries: [],
});

const findByName =
  (name: string) =>
  ({ value }: RequestGuideAnswer) =>
    value === name;

class QuestionDraftStateReducer extends ImmerReducer<QuestionState> {
  setQuestion(text: string) {
    this.draftState.question = text;
  }

  setType(type: QuestionTypes) {
    this.draftState.type = type;
    this.draftState.answers = [];

    if (type === QUESTION_TYPES.single || type === QUESTION_TYPES.multiple) {
      this.addAnswer();
    }
  }

  deleteServiceType(serviceType: string) {
    this.draftState.serviceTypes = this.draftState.serviceTypes.filter((item) => item.name !== serviceType);

    this.draftState.initialServiceTypes = this.draftState.initialServiceTypes.filter(
      (item) => item.name !== serviceType
    );

    this.draftState.answers = this.draftState.answers.filter((item) => item.value !== serviceType);
  }

  setAnswer(type: AnswerTypes) {
    this.draftState.answers = [];
    this.draftState.answerType = type;

    if (type === ANSWER_TYPES.custom) {
      this.draftState.industries = [];
      this.draftState.serviceTypes = [];
    }

    if (type === ANSWER_TYPES.serviceTypes) {
      this.draftState.serviceTypes = this.draftState.initialServiceTypes.map((element) => ({
        ...element,
        enabled: true,
        toggleEnabled: true,
        textEnabled: false,
        canDelete: true,
      }));
      this.draftState.industries = [];
    }

    if (type === ANSWER_TYPES.industries) {
      this.draftState.industries = this.draftState.initialIndustries.map((element) => ({
        ...element,
        enabled: true,
        toggleEnabled: true,
        textEnabled: false,
        canDelete: false,
      }));
      this.draftState.serviceTypes = [];
    }
  }

  setReminder(text: string) {
    this.draftState.reminder = text;
  }

  deleteAction(indexToRemove: number) {
    this.draftState.answers = this.state.answers.filter((_, index) => index !== indexToRemove);
  }

  setAnswersOrder(answers: RequestGuideAnswer[]) {
    this.draftState.answers = answers;
  }

  addAnswer() {
    this.draftState.answers.push({
      uid: replaceDash(v4()),
      value: '',
    });
  }

  addServiceType() {
    this.draftState.serviceTypes.push({
      uuid: replaceDash(v4()),
      name: '',
      enabled: true,
      textEnabled: true,
      toggleEnabled: true,
      canDelete: true,
    });
  }

  setInitialAnswers(industries: PriceBookIndustry[], serviceTypes: ServiceType[], isEdit: boolean) {
    this.draftState.initialIndustries = industries;
    this.draftState.initialServiceTypes = serviceTypes;

    const restOfServiceTypes = this.draftState.serviceTypes
      .filter(({ name }) => !serviceTypes.find(({ name: name2 }) => name === name2))
      .map((serviceType) => ({ ...serviceType, disabled: true }));

    this.draftState.serviceTypes = serviceTypes
      .map<Switchable<ServiceType>>((serviceType) => ({
        ...serviceType,
        disabled: true,
        enabled: !isEdit ? true : this.hasAnswer(serviceType.name),
        toggleEnabled: !isEdit,
        textEnabled: false,
        canDelete: true,
      }))
      .concat(restOfServiceTypes);

    const restOfIndustries = this.draftState.industries.filter(
      ({ name }) => !industries.find(({ name: name2 }) => name === name2)
    );

    this.draftState.industries = industries
      .map<Switchable<PriceBookIndustry>>((industry) => ({
        ...industry,
        textEnabled: false,
        toggleEnabled: !isEdit,
        enabled: !isEdit ? true : this.hasAnswer(industry.name),
        canDelete: false,
      }))
      .concat(restOfIndustries);

    if (this.state.answerType === ANSWER_TYPES.serviceTypes) {
      const serviceTypesFromAnswers: Array<Switchable<ServiceType>> =
        this.state.answers?.map(({ value, uid }) => ({
          name: value,
          enabled: true,
          uuid: uid,
          textEnabled: false,
          toggleEnabled: false,
          canDelete: true,
        })) ?? [];

      const uniqueServiceTypesNotAddedInAnswers = this.draftState.serviceTypes.filter(
        ({ name }) =>
          !serviceTypesFromAnswers.some((serviceType) => serviceType.name.toLowerCase() === name.toLowerCase())
      );

      this.draftState.serviceTypes = serviceTypesFromAnswers.concat(uniqueServiceTypesNotAddedInAnswers);
    }

    if (this.state.answerType === ANSWER_TYPES.industries) {
      const industriesFromAnswers: Array<Switchable<PriceBookIndustry>> =
        this.state.answers?.map(({ value, uid }) => ({
          name: value,
          uuid: uid,
          image: '',
          enabled: true,
          toggleEnabled: false,
          textEnabled: false,
          canDelete: false,
        })) ?? [];

      const uniqueIndustries = this.draftState.industries.filter(
        ({ name }) => !industriesFromAnswers.some((industry) => industry.name.toLowerCase() === name.toLowerCase())
      );

      this.draftState.industries = industriesFromAnswers.concat(uniqueIndustries);
    }
  }

  private hasAnswer(name: string) {
    return !!this.draftState.answers.find(findByName(name));
  }

  setServiceTypes(serviceTypes: Switchable<ServiceType>[]) {
    this.draftState.serviceTypes = serviceTypes;
  }

  setIndustries(industries: Switchable<PriceBookIndustry>[]) {
    this.draftState.industries = industries;
  }

  toggleAnswer(type: 'serviceTypes' | 'industries', name: string, checked: boolean) {
    const index = this.draftState[type].findIndex((element) => element.name === name);
    const answer = this.draftState[type][index];

    if (answer) {
      this.draftState[type][index] = {
        ...answer,
        enabled: checked,
      };
    }
  }

  setAnswerContent(index: number, text: string) {
    const answer = this.draftState.answers[index];

    if (answer) {
      answer.value = text;
    }
  }
}

export const useQuestionDraftStateActions = createActionCreators(QuestionDraftStateReducer);
const useQuestionDraftStateReducer = createReducerFunction(QuestionDraftStateReducer);

export const useQuestionDraftState = (initialValues?: Partial<QuestionState>) =>
  useReducer(useQuestionDraftStateReducer, { ...getInitialState(), ...initialValues });
